import React, { useState } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import StudentSearchService from "../../_services/StudentSearchService";

interface ExcelExportModalProps {
  show: boolean;
  onHide: () => void;
  searchParams: any;
  lookups: any;
}

const ExcelExportModal: React.FC<ExcelExportModalProps> = ({
  show,
  onHide,
  searchParams,
  lookups,
}) => {
  // Use dynamic columns from lookups if available, otherwise fall back to empty array
  const AVAILABLE_COLUMNS = lookups?.excelColumns || [];
  const [selectedCols, setSelectedCols] = useState<Set<string>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const toggleCol = (name: string) => {
    setSelectedCols((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedCols.size === AVAILABLE_COLUMNS.length) {
      setSelectedCols(new Set());
    } else {
      setSelectedCols(new Set(AVAILABLE_COLUMNS.map((c) => c.name)));
    }
  };

  const handleExport = async () => {
    if (selectedCols.size === 0) {
      setError("Select at least one column to export.");
      return;
    }
    setExporting(true);
    setError("");
    try {
      const columns = AVAILABLE_COLUMNS.filter((c) => selectedCols.has(c.name));
      const resp = await StudentSearchService.exportExcel({
        searchParams,
        columns,
      });
      const url = resp.data?.data?.downloadUrl;
      if (url) {
        window.open(url, "_blank");
        onHide();
      } else {
        setError("Export completed but no download URL was returned.");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Excel Export</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: 4,
              marginBottom: 10,
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {AVAILABLE_COLUMNS.length === 0 ? (
          <div style={{ padding: "20px", textAlign: "center", color: "var(--text-muted)" }}>
            No export columns configured. Please contact your administrator.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 8 }}>
              <Form.Check
                inline
                type="checkbox"
                label={<span style={{ fontWeight: 700, fontSize: 13 }}>Select All</span>}
                checked={selectedCols.size === AVAILABLE_COLUMNS.length}
                onChange={toggleAll}
              />
            </div>

            <Row>
              {AVAILABLE_COLUMNS.map((col) => (
                <Col sm={3} key={col.id} style={{ padding: "4px 8px" }}>
                  <Form.Check
                    type="checkbox"
                    label={<span style={{ fontSize: 12 }}>{col.displayLabel}</span>}
                    checked={selectedCols.has(col.name)}
                    onChange={() => toggleCol(col.name)}
                  />
                </Col>
              ))}
            </Row>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          size="sm"
          onClick={handleExport}
          disabled={exporting || AVAILABLE_COLUMNS.length === 0}
        >
          {exporting ? "Exporting..." : "Export"}
        </Button>
        <Button variant="secondary" size="sm" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExcelExportModal;
